import { publicRequest } from '../requestMethods';

// Cache for domain to sellerID mappings
const domainCache = new Map();

// ============================================
// TEMPORARY TEST MODE - Remove before production
// ============================================
// Set to true to test custom domain functionality on localhost
const TEST_MODE = true;
const TEST_PORT = '3002'; // Only apply test mode to this port
const TEST_DOMAIN = 'naufalkareem.com';
const TEST_SELLER_ID = 'hyziandummi'; // The sellerID to use when testing
// ============================================

/**
 * Extracts the current domain from window.location.hostname
 * @returns {string} The current domain
 */
export const getCurrentDomain = () => {
  let domain = window.location.hostname.toLowerCase();
  const port = window.location.port;
  const href = window.location.href;
  
  // TEMPORARY TEST MODE: Treat localhost:3002 as test domain
  // Other ports (like 3003) will be treated as regular subfolder stores
  if (TEST_MODE && (domain === 'localhost' || domain === '127.0.0.1')) {
    // Check if URL contains :3002 (works even if port is not explicitly in window.location.port)
    if (port === TEST_PORT || href.includes(':' + TEST_PORT + '/') || href.includes(':' + TEST_PORT)) {
      domain = TEST_DOMAIN;
    }
  }
  
  return domain;
};

/**
 * Checks if the domain is a custom domain (not prodinent.com or localhost)
 * @param {string} domain - The domain to check
 * @returns {boolean} True if it's a custom domain
 */
export const isCustomDomain = (domain) => {
  if (!domain) {
    return false;
  }
  const normalizedDomain = domain.toLowerCase();
  const port = window.location.port;
  const href = window.location.href;
  
  // TEMPORARY TEST MODE: Only treat localhost:3002 as custom domain
  // Other ports (like 3003) should be treated as regular subfolder stores
  if (TEST_MODE) {
    if (normalizedDomain === TEST_DOMAIN) {
      return true;
    }
    // Only apply test mode to port 3002
    // Check both port and URL to handle cases where port might not be in window.location.port
    const isPort3002 = port === TEST_PORT || href.includes(':' + TEST_PORT + '/') || href.includes(':' + TEST_PORT);
    if ((normalizedDomain === 'localhost' || normalizedDomain === '127.0.0.1') && isPort3002) {
      return true;
    }
    // If localhost but different port, treat as regular subfolder store
    if ((normalizedDomain === 'localhost' || normalizedDomain === '127.0.0.1') && !isPort3002) {
      return false;
    }
  }
  
  const isCustom = !normalizedDomain.includes('prodinent.com') && 
                   !normalizedDomain.includes('localhost') && 
                   normalizedDomain !== '127.0.0.1';
  return isCustom;
};

/**
 * Resolves sellerID from a custom domain by calling the API
 * @param {string} domain - The custom domain
 * @returns {Promise<string|null>} The sellerID or null if not found
 */
export const resolveSellerIDFromDomain = async (domain) => {
  
  if (!domain || !isCustomDomain(domain)) {
    return null;
  }

  // TEMPORARY TEST MODE: Return test sellerID for localhost:3002 only
  const port = window.location.port;
  const href = window.location.href;
  const isPort3002 = port === TEST_PORT || href.includes(':' + TEST_PORT + '/') || href.includes(':' + TEST_PORT);
  
  if (TEST_MODE) {
    if (domain === TEST_DOMAIN) {
      domainCache.set(domain, TEST_SELLER_ID);
      return TEST_SELLER_ID;
    }
    // Only apply test mode to port 3002
    if ((domain === 'localhost' || domain === '127.0.0.1') && isPort3002) {
      domainCache.set(domain, TEST_SELLER_ID);
      return TEST_SELLER_ID;
    }
  }

  // Check cache first
  if (domainCache.has(domain)) {
    const cachedSellerID = domainCache.get(domain);
    return cachedSellerID;
  }

  try {
    const apiUrl = `/getSeller/${domain}`;
    
    const response = await publicRequest.get(apiUrl, {
      withCredentials: true,
    });

    
    const sellerID = response.data?.sellerID || null;
    
    if (sellerID) {
      domainCache.set(domain, sellerID);
    } else {
    }
    
    return sellerID;
  } catch (error) {
    return null;
  }
};

function isLocalhostTestPort() {
  const port = window.location.port;
  const href = window.location.href;
  return port === TEST_PORT || href.includes(':' + TEST_PORT + '/') || href.includes(':' + TEST_PORT);
}

/**
 * sellerID hint for branded loaders before async domain→seller resolution finishes.
 * Uses URL path (/reemjewels) or localhost test port (TEST_SELLER_ID).
 */
export const getLoaderSellerIDHint = (pathname = window.location.pathname) => {
  if (TEST_MODE && isLocalhostTestPort()) {
    const host = window.location.hostname.toLowerCase();
    if (host === 'localhost' || host === '127.0.0.1') {
      return TEST_SELLER_ID;
    }
  }
  return extractSellerIDFromPath(pathname);
};

/**
 * Extracts sellerID from URL path (for subfolder-based stores)
 * @param {string} pathname - The URL pathname
 * @returns {string|null} The sellerID or null
 */
export const extractSellerIDFromPath = (pathname) => {
  
  if (!pathname) {
    return null;
  }
  
  // Remove leading slash and split
  const parts = pathname.split('/').filter(part => part.length > 0);
  
  // First part should be sellerID (unless it's a reserved route)
  const reservedRoutes = ['checkout', 'categories', 'policies', 'new-customer', 'track', 'review', 'pages', 'login'];
  
  if (parts.length > 0 && !reservedRoutes.includes(parts[0].toLowerCase())) {
    const sellerID = parts[0].toLowerCase();
    return sellerID;
  }
  
  return null;
};

/**
 * Main function to resolve sellerID from either domain or URL path
 * @returns {Promise<string|null>} The resolved sellerID
 */
export const resolveSellerID = async () => {
  const domain = getCurrentDomain();
  const pathname = window.location.pathname;
  const fullUrl = window.location.href;
  
  //   domain,
  //   pathname,
  //   fullUrl,
  //   protocol: window.location.protocol,
  //   port: window.location.port
  // });
  
  // If custom domain, resolve from domain
  if (isCustomDomain(domain)) {
    const sellerID = await resolveSellerIDFromDomain(domain);
    if (sellerID) {
      return sellerID;
    } else {
    }
  } else {
  }
  
  // Fallback to URL path extraction
  const pathSellerID = extractSellerIDFromPath(pathname);
  return pathSellerID;
};

/**
 * Clears the domain cache (useful for testing or when domains are updated)
 */
export const clearDomainCache = () => {
  domainCache.clear();
};

/**
 * Checks if current domain is custom domain
 * @returns {boolean} True if custom domain
 */
const checkIsCustomDomain = () => {
  const domain = getCurrentDomain();
  return isCustomDomain(domain);
};

/**
 * Generates store URL based on domain type
 * For custom domains: /
 * For subfolder URLs: /sellerID
 * @param {string} sellerID - The seller ID
 * @returns {string} The store URL
 */
export const getStoreUrl = (sellerID) => {
  if (!sellerID) {
    return '/';
  }
  
  const isCustom = checkIsCustomDomain();
  
  if (isCustom) {
    // Custom domain: just /
    const url = '/';
    return url;
  } else {
    // Subfolder: /sellerID
    const url = `/${sellerID}`;
    return url;
  }
};

/**
 * Generates product URL based on domain type
 * For custom domains: /productID
 * For subfolder URLs: /sellerID/productID
 * @param {string} sellerID - The seller ID
 * @param {string} productID - The product ID
 * @returns {string} The product URL
 */
export const getProductUrl = (sellerID, productID) => {
  if (!sellerID || !productID) {
    return '#';
  }
  
  const isCustom = checkIsCustomDomain();
  
  if (isCustom) {
    // Custom domain: just /productID
    const url = `/${productID}`;
    return url;
  } else {
    // Subfolder: /sellerID/productID
    const url = `/${sellerID}/${productID}`;
    return url;
  }
};

/**
 * Generates checkout URL based on domain type
 * For custom domains: /checkout or /checkout/:productIDs
 * For subfolder URLs: /sellerID/checkout or /sellerID/checkout/:productIDs
 * @param {string} sellerID - The seller ID
 * @param {string} productIDs - Optional product IDs for direct checkout
 * @returns {string} The checkout URL
 */
export const getCheckoutUrl = (sellerID, productIDs = null) => {
  if (!sellerID) {
    return '/checkout';
  }
  
  const isCustom = checkIsCustomDomain();
  
  if (isCustom) {
    // Custom domain: /checkout or /checkout/:productIDs
    const url = productIDs ? `/checkout/${productIDs}` : '/checkout';
    return url;
  } else {
    // Subfolder: /sellerID/checkout or /sellerID/checkout/:productIDs
    const url = productIDs ? `/${sellerID}/checkout/${productIDs}` : `/${sellerID}/checkout`;
    return url;
  }
};

/**
 * Generates categories URL based on domain type
 * For custom domains: /categories or /categories?category=...
 * For subfolder URLs: /sellerID/categories or /sellerID/categories?category=...
 * @param {string} sellerID - The seller ID
 * @param {string} category - Optional category name
 * @returns {string} The categories URL
 */
export const getCategoriesUrl = (sellerID, category = null) => {
  if (!sellerID) {
    return '/categories';
  }
  
  const isCustom = checkIsCustomDomain();
  let baseUrl;
  
  if (isCustom) {
    // Custom domain: /categories
    baseUrl = '/categories';
  } else {
    // Subfolder: /sellerID/categories
    baseUrl = `/${sellerID}/categories`;
  }
  
  // Add category query param if provided
  if (category) {
    const encodedCategory = encodeURIComponent(category);
    const url = `${baseUrl}?category=${encodedCategory}`;
    return url;
  }
  
  return baseUrl;
};

/**
 * Generates review URL based on domain type
 * For custom domains: /review/:productID
 * For subfolder URLs: /sellerID/review/:productID
 * @param {string} sellerID - The seller ID
 * @param {string} productID - The product ID
 * @returns {string} The review URL
 */
export const getReviewUrl = (sellerID, productID) => {
  if (!sellerID || !productID) {
    return '#';
  }
  
  const isCustom = checkIsCustomDomain();
  
  if (isCustom) {
    // Custom domain: /review/:productID
    const url = `/review/${productID}`;
    return url;
  } else {
    // Subfolder: /sellerID/review/:productID
    const url = `/${sellerID}/review/${productID}`;
    return url;
  }
};

/**
 * Generates policy URL based on domain type
 * For custom domains: /policies/:policyType
 * For subfolder URLs: /sellerID/policies/:policyType
 * @param {string} sellerID - The seller ID
 * @param {string} policyType - The policy type (e.g., 'shipping', 'privacy', 'terms')
 * @returns {string} The policy URL
 */
export const getPolicyUrl = (sellerID, policyType) => {
  if (!sellerID || !policyType) {
    return '#';
  }
  
  const isCustom = checkIsCustomDomain();
  
  if (isCustom) {
    // Custom domain: /policies/:policyType
    const url = `/policies/${policyType}`;
    return url;
  } else {
    // Subfolder: /sellerID/policies/:policyType
    const url = `/${sellerID}/policies/${policyType}`;
    return url;
  }
};

const STATIC_PAGE_SLUGS = new Set(['contact', 'about', 'faq']);

/**
 * URL for built-in page builder pages (Contact, About, FAQ).
 * Custom domain: /pages/:slug — Subfolder: /:sellerID/pages/:slug
 */
export const getStaticContentPageUrl = (sellerID, pageSlug) => {
  if (!sellerID || !pageSlug || !STATIC_PAGE_SLUGS.has(String(pageSlug).toLowerCase())) {
    return '#';
  }
  const slug = String(pageSlug).toLowerCase();
  const isCustom = checkIsCustomDomain();
  if (isCustom) {
    return `/pages/${slug}`;
  }
  return `/${sellerID}/pages/${slug}`;
};

/**
 * Generates track order URL based on domain type
 * For custom domains: /track or /track/:orderID
 * For subfolder URLs: /sellerID/track or /sellerID/track/:orderID
 * @param {string} sellerID - The seller ID
 * @param {string} orderID - Optional order ID
 * @returns {string} The track order URL
 */
export const getTrackOrderUrl = (sellerID, orderID = null) => {
  if (!sellerID) {
    const isCustom = checkIsCustomDomain();
    return isCustom ? '/track' : '/track';
  }
  
  const isCustom = checkIsCustomDomain();
  
  if (isCustom) {
    // Custom domain: /track or /track/:orderID
    const url = orderID ? `/track/${orderID}` : '/track';
    return url;
  } else {
    // Subfolder: /sellerID/track or /sellerID/track/:orderID
    const url = orderID ? `/${sellerID}/track/${orderID}` : `/${sellerID}/track`;
    return url;
  }
};
